import { Box, Typography } from '@mui/material';
import { Children, type ReactNode } from 'react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LinkifiedLegalText } from '../rich-text/linkified-legal-text';

/**
 * Markdown rendering for assistant replies, tuned for a chat bubble.
 *
 * Safety: react-markdown never executes raw HTML (no rehype-raw here), so
 * model output cannot inject markup — unknown tags render as plain text.
 * The system prompt only allows the subset below (paragraphs, lists, bold,
 * italics, inline code); the extra mappings are defensive, so an off-script
 * heading or table still renders readably instead of as raw syntax.
 */

/**
 * Applies `LinkifiedLegalText` (the shared catalog matcher —
 * `.claude/rules/legal-references.md`) to every plain-STRING child, leaving
 * nested elements (bold/italic/code/links) untouched: only the known
 * citation patterns are ever linked, never a URL the model itself wrote.
 */
function linkifyTextChildren(children: ReactNode): ReactNode {
  // `Children.map` assigns its own stable per-child key internally — an
  // index-derived key here would fight it (`noArrayIndexKey`) for no reason.
  return Children.map(children, (child) =>
    typeof child === 'string' ? <LinkifiedLegalText text={child} /> : child,
  );
}

const components: Components = {
  p: ({ children }) => (
    <Typography
      sx={{
        color: 'text.primary',
        lineHeight: 1.6,
        mb: 0.75,
        overflowWrap: 'anywhere',
        // Legacy persisted replies rely on literal line breaks (markdown needs
        // blank lines for paragraphs): pre-wrap keeps them visible.
        whiteSpace: 'pre-wrap',
        '&:last-child': { mb: 0 },
      }}
      variant="body2"
    >
      {linkifyTextChildren(children)}
    </Typography>
  ),
  strong: ({ children }) => (
    <Typography component="strong" sx={{ color: 'text.primary', fontWeight: 700 }}>
      {children}
    </Typography>
  ),
  em: ({ children }) => (
    <Typography component="em" sx={{ fontStyle: 'italic' }}>
      {children}
    </Typography>
  ),
  code: ({ children }) => (
    <Box
      component="code"
      sx={{
        bgcolor: 'action.hover',
        borderRadius: 0.5,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '0.85em',
        overflowWrap: 'anywhere',
        px: 0.5,
        py: '1px',
      }}
    >
      {children}
    </Box>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ m: 0, mb: 0.75, pl: 2.5, '&:last-child': { mb: 0 } }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ m: 0, mb: 0.75, pl: 2.5, '&:last-child': { mb: 0 } }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography
      component="li"
      sx={{
        color: 'text.primary',
        lineHeight: 1.55,
        mb: 0.25,
        overflowWrap: 'anywhere',
        '&::marker': { color: 'text.secondary' },
      }}
      variant="body2"
    >
      {linkifyTextChildren(children)}
    </Typography>
  ),
  a: ({ children, href }) => (
    <Typography
      component="a"
      href={href}
      rel="noopener noreferrer"
      sx={{ color: 'primary.main', fontWeight: 600 }}
      target="_blank"
      variant="body2"
    >
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography sx={{ color: 'text.primary', fontWeight: 700, mt: 1, mb: 0.5 }} variant="body2">
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography sx={{ color: 'text.primary', fontWeight: 700, mt: 1, mb: 0.5 }} variant="body2">
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography sx={{ color: 'text.primary', fontWeight: 600, mt: 0.75, mb: 0.5 }} variant="body2">
      {children}
    </Typography>
  ),
};

export function AssistantMarkdown({ text }: { text: string }) {
  return (
    <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
      {text}
    </ReactMarkdown>
  );
}
